import React, { useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import { MultiValue } from 'react-select';
import { useQuery } from '@tanstack/react-query';
import { listSubjectTags } from '../../services/SubjectService';
import { queryKeys } from '../../app/queryKeys';
import { creatableMultiSelectStyles, type CreatableOption } from '../selects/creatableSelectStyles';

type TagOption = CreatableOption;

interface SubjectTagMultiSelectProps {
    classroomId?: string | null;
    value: string[] | null;
    onChange: (next: string[] | null) => void;
    placeholder?: string;
    disabled?: boolean;
    isSmall?: boolean;
    /** When true, teachers can type new section tags (default: true). */
    allowCreate?: boolean;
}

const SubjectTagMultiSelect: React.FC<SubjectTagMultiSelectProps> = ({
    classroomId,
    value,
    onChange,
    placeholder = 'Add tags, e.g. Sciences, Languages',
    disabled = false,
    isSmall = false,
    allowCreate = true,
}) => {
    const { data: tags = [], isLoading } = useQuery({
        queryKey: queryKeys.subjects.tags(classroomId),
        queryFn: () => listSubjectTags(classroomId ?? undefined),
        staleTime: 60_000,
    });

    const options = useMemo<TagOption[]>(() => {
        const fromApi = tags.map((t) => ({
            value: t.tag,
            label: t.subject_count > 0 ? `${t.tag} (${t.subject_count})` : t.tag,
        }));
        const seen = new Set(fromApi.map((o) => o.value.toLowerCase()));
        const extras = (value ?? [])
            .filter((tag) => !seen.has(tag.toLowerCase()))
            .map((tag) => ({ value: tag, label: tag }));
        return [...fromApi, ...extras];
    }, [tags, value]);

    const selected = useMemo<TagOption[]>(
        () => (value ?? []).map((tag) => ({ value: tag, label: tag })),
        [value],
    );

    const handleChange = (next: MultiValue<TagOption>) => {
        if (!next || next.length === 0) {
            onChange(null);
            return;
        }
        onChange(next.map((opt) => opt.value));
    };

    const handleCreate = (inputValue: string) => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        const current = value ?? [];
        if (current.some((t) => t.toLowerCase() === trimmed.toLowerCase())) return;
        onChange([...current, trimmed]);
    };

    return (
        <CreatableSelect<TagOption, true>
            isMulti
            options={options}
            value={selected}
            onChange={handleChange}
            onCreateOption={allowCreate ? handleCreate : undefined}
            isDisabled={disabled || isLoading}
            placeholder={isLoading ? 'Loading tags…' : placeholder}
            styles={creatableMultiSelectStyles(isSmall)}
            classNamePrefix="subject-tag-select"
            formatCreateLabel={(input) => `Add "${input}"`}
            isClearable
        />
    );
};

export default SubjectTagMultiSelect;
